import { useMemo, useState } from "react";
import {
  Page,
  Card,
  IndexTable,
  Text,
  Badge,
  InlineStack,
  BlockStack,
  Select,
  TextField,
  Button,
  Banner,
  EmptyState,
  Box,
  Checkbox,
} from "@shopify/polaris";
import moment from "moment";
import "moment/locale/es";
import AppWrapper from "../AppWrapper";
import { formatPrice } from "../../utility/funcs";
import { getStatement, speiEnabled } from "../../utility/pagos";
import {
  ACCOUNTS,
  accountLabel,
  annotateStatement,
  downloadStatementXlsx,
  loadConsolidated,
  mtyDisplay,
  visibleRows,
} from "../../utility/fintocStatement";

// Estado de cuenta of one Fintoc account, with the columns a bank statement
// cannot have: whose payment it was, which factura / reservation it covered,
// which provider service or traslado a transfer paid. The movements and
// balances are Fintoc's (via pagos-svc); the annotations are ours (Supabase).
//
// "Todas las cuentas" merges the five into one statement; transfers between
// them are paired and, by default, hidden — they move money inside Fintoc
// without changing what GoMove has there.

const ALL = "all";

const money = (c) => (c == null ? "—" : formatPrice(c / 100));

// IndexTable cells never wrap by default, which is what pushed the table past
// the page width. Text columns wrap instead; long conceptos stop at 2 lines.
const Wrap = ({ children }) => (
  <div style={{ whiteSpace: "normal", overflowWrap: "anywhere", minWidth: 160, maxWidth: 420 }}>
    {children}
  </div>
);
const CLAMP_2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

function FintocStatement() {
  const [account, setAccount] = useState(ALL);
  const [from, setFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { statement, annotated }
  const [showInternal, setShowInternal] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      if (account === ALL) {
        setResult(await loadConsolidated(getStatement, from, to));
      } else {
        const statement = await getStatement(account, from, to);
        const annotated = await annotateStatement(statement);
        setResult({ statement, annotated });
      }
    } catch (err) {
      setError(
        err.code === "forbidden"
          ? "No tienes el permiso treasury.statements para ver estados de cuenta."
          : err.message
      );
    } finally {
      setLoading(false);
    }
  };

  const s = result?.statement.summary;
  const consolidated = !!result?.statement.consolidated;
  const shown = useMemo(
    () => (result ? visibleRows(result.statement, result.annotated, showInternal) : []),
    [result, showInternal]
  );
  const hiddenCount = result ? result.statement.rows.length - shown.length : 0;

  return (
    <AppWrapper>
      <Page title="Estado de cuenta Fintoc" fullWidth>
        <BlockStack gap="400">
          {!speiEnabled() && (
            <Banner tone="warning">El servicio de pagos no está configurado (REACT_APP_PAGOS_URL).</Banner>
          )}
          <Card>
            <InlineStack gap="300" blockAlign="end" wrap>
              <div style={{ minWidth: 240 }}>
                <Select
                  label="Cuenta"
                  options={[
                    { label: "Todas las cuentas (consolidado)", value: ALL },
                    ...ACCOUNTS.map((a) => ({ label: a.label, value: a.key })),
                  ]}
                  value={account}
                  onChange={setAccount}
                />
              </div>
              <TextField label="Desde" type="date" value={from} onChange={setFrom} autoComplete="off" />
              <TextField label="Hasta" type="date" value={to} onChange={setTo} autoComplete="off" />
              <Button variant="primary" onClick={load} loading={loading} disabled={!speiEnabled()}>
                Consultar
              </Button>
              {result && (
                <Button
                  onClick={() => downloadStatementXlsx(result.statement, result.annotated, { showInternal })}
                >
                  Descargar Excel
                </Button>
              )}
            </InlineStack>
            <Box paddingBlockStart="300">
              <Checkbox
                label="Mostrar transferencias entre cuentas"
                helpText="Traspasos entre las cuentas Fintoc de GoMove. Los fondeos desde Afirme siempre se muestran."
                checked={showInternal}
                onChange={setShowInternal}
              />
            </Box>
          </Card>

          {error && <Banner tone="critical">{error}</Banner>}

          {s && (
            <Card>
              <InlineStack gap="300" blockAlign="center" wrap>
                {s.balanced === false ? (
                  <Badge tone="critical">No cuadra</Badge>
                ) : (
                  <Badge tone="success">Cuadra</Badge>
                )}
                <Text as="span">
                  {shown.length} movimientos
                  {hiddenCount > 0 ? ` (${hiddenCount} traspasos entre cuentas ocultos)` : ""} · saldo
                  inicial {money(s.opening_cents)} · saldo final {money(s.closing_cents)}
                </Text>
              </InlineStack>
              {s.balanced === false && (
                <Box>
                  <Text as="p" tone="critical">
                    Saldo inicial + abonos ({money(s.inbound_cents)}) − cargos ({money(s.outbound_cents)}) no
                    da el saldo final que reporta Fintoc. Revisa el periodo o avisa a soporte.
                  </Text>
                </Box>
              )}
            </Card>
          )}

          {result && (
            <Card padding="0">
              {shown.length === 0 ? (
                <EmptyState heading="Sin movimientos en el periodo" image="" />
              ) : (
                <IndexTable
                  resourceName={{ singular: "movimiento", plural: "movimientos" }}
                  itemCount={shown.length}
                  selectable={false}
                  headings={[
                    { title: "Fecha" },
                    { title: "Movimiento" },
                    { title: "Factura / Reservación" },
                    { title: "Cargo", alignment: "end" },
                    { title: "Abono", alignment: "end" },
                    { title: consolidated ? "Saldo consolidado" : "Saldo", alignment: "end" },
                  ]}
                >
                  {shown.map(({ r, a }, i) => {
                    const unknown = a.category.includes("sin identificar");
                    const [day, time] = mtyDisplay(r.transaction_date).split(" ");
                    const party = a.party || r.transfer?.counterparty_name;
                    return (
                      <IndexTable.Row id={r.movement_id} key={r.movement_id} position={i}>
                        <IndexTable.Cell>
                          <Text as="p">{day}</Text>
                          <Text as="p" tone="subdued" variant="bodySm">
                            {time}
                            {consolidated ? ` · ${accountLabel(r.account)}` : ""}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Wrap>
                            {unknown ? (
                              <Badge tone="attention">{a.category}</Badge>
                            ) : (
                              <Text as="p" fontWeight="medium">{a.category}</Text>
                            )}
                            {party && <Text as="p">{party}</Text>}
                            {r.transfer?.comment && (
                              <Text as="p" tone="subdued" variant="bodySm">
                                <span style={CLAMP_2}>{r.transfer.comment}</span>
                              </Text>
                            )}
                          </Wrap>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Wrap>
                            {a.invoices && <Text as="p">{a.invoices}</Text>}
                            {a.suggested && (
                              <InlineStack gap="100" blockAlign="center">
                                <Text as="span" tone="subdued">{a.suggested}</Text>
                                <Badge tone="info">sugerida</Badge>
                              </InlineStack>
                            )}
                            {a.reservations !== "" && a.reservations != null && (
                              <Text as="p" tone="subdued" variant="bodySm">
                                Res. {a.reservations}
                              </Text>
                            )}
                            {!a.invoices && !a.suggested && !a.reservations && (
                              <Text as="span" tone="subdued">—</Text>
                            )}
                          </Wrap>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end" numeric>
                            {r.signed_cents < 0 ? money(-r.signed_cents) : ""}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end" numeric>
                            {r.signed_cents > 0 ? money(r.signed_cents) : ""}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end" numeric>
                            {money(consolidated ? r.consolidated_balance_cents : r.balance_cents)}
                          </Text>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              )}
            </Card>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default FintocStatement;
